import {
  CustomQuestion,
  CustomSection,
  FixedField,
} from 'domain/feature/hub/company/meeting-room/settings/ApplicationFormsTab/types'
import {
  mapGetResponseToFormState,
  MeetingRoomSettingsGetData,
  MeetingRoomSettingsGetResponse,
} from 'domain/feature/hub/company/meeting-room/settings/api/mappers'
import { IMeetingRoomSettingsDays } from 'domain/feature/meeting-room/shared/types'

export interface IPublicFormSettings {
  checkboxText: string
  checkboxRequired: boolean
  warningText: string
  fixedFields: FixedField[]
  sections: CustomSection[]
  workingDays: IMeetingRoomSettingsDays
  openTime: string
  closeTime: string
  meetingSpacesLimit: number
  meetingDaysLimit: number
  meetingMinLeadTime: number
  meetingMaxDuration: number
  meetingAutomaticApproval: boolean
  eventSpacesLimit: number
  eventDaysLimit: number
  eventMinLeadTime: number
  eventInterestDemonstration: boolean
}

const hasQuestionText = (question: CustomQuestion): boolean => question.text.trim() !== ''

const isSectionVisibleForPublicEvent = (section: CustomSection): boolean =>
  section.eventChecked === true || section.meetingChecked !== true

const normalizeSections = (sections: CustomSection[]): CustomSection[] =>
  sections
    .filter(isSectionVisibleForPublicEvent)
    .map(section => ({
      ...section,
      questions: section.questions.filter(hasQuestionText),
    }))
    .filter(section => section.questions.length > 0)

const toSettingsData = (value: Record<string, unknown>): MeetingRoomSettingsGetData =>
  value as unknown as MeetingRoomSettingsGetData

const extractSettingsPayload = (response: unknown): MeetingRoomSettingsGetResponse | null => {
  if (response == null || typeof response !== 'object') return null

  const raw = response as Record<string, unknown>

  const hasSettingsShape = (obj: Record<string, unknown>): boolean =>
    'sections' in obj ||
    'Sections' in obj ||
    'fixedFields' in obj ||
    'FixedFields' in obj

  if (hasSettingsShape(raw)) {
    return {
      id: String(raw.id ?? raw.Id ?? ''),
      data: toSettingsData(raw),
    }
  }

  const nested = raw.data ?? raw.Data

  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>
    const nestedHasSettingsShape =
      'sections' in nestedRecord ||
      'Sections' in nestedRecord ||
      'fixedFields' in nestedRecord ||
      'FixedFields' in nestedRecord

    if (nestedHasSettingsShape) {
      return {
        id: String(nestedRecord.id ?? nestedRecord.Id ?? ''),
        data: toSettingsData(nestedRecord),
      }
    }

    const innerData = nestedRecord.data ?? nestedRecord.Data
    if (innerData != null && typeof innerData === 'object') {
      return {
        id: String(nestedRecord.id ?? nestedRecord.Id ?? ''),
        data: toSettingsData(innerData as Record<string, unknown>),
      }
    }
  }

  return null
}

export const normalizePublicSettingsResponse = (response: unknown): IPublicFormSettings | null => {
  const payload = extractSettingsPayload(response)
  if (payload == null) return null

  const mapped = mapGetResponseToFormState(payload)

  return {
    checkboxText: mapped.formsData.checkboxText,
    checkboxRequired: mapped.formsData.checkboxRequired,
    warningText: mapped.formsData.warningText,
    fixedFields: mapped.formsData.fixedFields,
    sections: normalizeSections(mapped.formsData.sections),
    workingDays: mapped.rulesData.days,
    openTime: mapped.rulesData.openTime,
    closeTime: mapped.rulesData.closeTime,
    meetingSpacesLimit: Number(mapped.rulesData.meetingSpacesLimit) || 1,
    meetingDaysLimit: Number(mapped.rulesData.meetingDaysLimit) || 1,
    meetingMinLeadTime: Number(mapped.rulesData.meetingMinLeadTime) || 0,
    meetingMaxDuration: Number(mapped.rulesData.meetingMaxDuration) || 0,
    meetingAutomaticApproval: mapped.rulesData.meetingAutomaticApproval === true,
    eventSpacesLimit: Number(mapped.rulesData.eventSpacesLimit) || 1,
    eventDaysLimit: Number(mapped.rulesData.eventDaysLimit) || 1,
    eventMinLeadTime: Number(mapped.rulesData.eventMinLeadTime) || 0,
    eventInterestDemonstration: mapped.rulesData.eventInterestDemonstration === true,
  }
}
